import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { TimePicker } from "./ui/time-picker";
import { formatTime, calculateDuration, calculateDurationHours, timeToMinutes, minutesToTime, doTimesOverlap, getCurrencySymbol, getRateValue, cn } from "../lib/utils";
import { Clock, Trash2, AlertTriangle, Coffee, Utensils, Plus, X, Split, DollarSign, Lock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

// Icon mapping
const BREAK_ICON_MAP = {
  coffee: Coffee,
  utensils: Utensils,
  clock: Clock,
};

const getBreakIcon = (iconName) => BREAK_ICON_MAP[iconName] || Coffee;

const ShiftEditDialog = ({ 
  open, 
  onOpenChange, 
  shift, 
  employee,
  employeeShifts = [], // All shifts for this employee on this date
  breakTypes = [], // Available break types from settings
  shiftPatterns = [], // Predetermined shift patterns
  onSave, 
  onDelete,
  businessHours,
  snapInterval = 15,
  shiftGapMinutes = 0,
  currency = "GBP",
  initialStartTime = null, // Time where user clicked on timeline
  initialEndTime = null, // Time where user released drag (for drag-to-create)
  isLocked = false // Whether this shift is on a locked (past) date
}) => {
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("18:00");
  const [breaks, setBreaks] = useState([]); // Array of { break_type_id, start_time, end_time }
  const [rateName, setRateName] = useState(null); // For non-split shifts
  const [rateSegments, setRateSegments] = useState([]); // For split shifts: [{start_time, end_time, rate_name}]
  const [selectedPatternId, setSelectedPatternId] = useState(null); // Track selected pattern

  // Snap time to interval
  const snapToInterval = useCallback((time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes;
    const snappedMinutes = Math.round(totalMinutes / snapInterval) * snapInterval;
    const newHours = Math.floor(snappedMinutes / 60) % 24;
    const newMinutes = snappedMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
  }, [snapInterval]);

  useEffect(() => {
    if (shift) {
      setStartTime(shift.start_time);
      setEndTime(shift.end_time);
      setBreaks(shift.breaks || []);
      setRateName(shift.rate_name || null);
      setRateSegments(shift.rate_segments || []);
      setSelectedPatternId(null);
    } else if (businessHours) {
      // Use clicked/dragged time if provided, otherwise default to open time
      const startMins = initialStartTime 
        ? timeToMinutes(initialStartTime)
        : timeToMinutes(businessHours.open_time);
      const closeMinutes = timeToMinutes(businessHours.close_time);
      
      // If initialEndTime is provided (drag-to-create), use it; otherwise default to 4 hours
      let endMins;
      if (initialEndTime) {
        endMins = timeToMinutes(initialEndTime);
      } else {
        // Default to 4 hours, but cap at close time
        endMins = Math.min(startMins + 240, closeMinutes);
      }
      
      setStartTime(initialStartTime || businessHours.open_time);
      setEndTime(minutesToTime(endMins));
      setBreaks([]);
      setRateName(null);
      setRateSegments([]);
      setSelectedPatternId(null);
    }
  }, [shift, businessHours, open, initialStartTime, initialEndTime]);

  // Apply a predetermined shift pattern
  const applyPattern = useCallback((patternId) => {
    if (!patternId || patternId === 'custom') {
      setSelectedPatternId(null);
      return;
    }
    
    const pattern = shiftPatterns.find(p => p.id === patternId);
    if (!pattern) return;
    
    setSelectedPatternId(patternId);
    setStartTime(pattern.start_time);
    setEndTime(pattern.end_time);
    
    // Add breaks from the pattern (with default times that can be adjusted)
    if (pattern.break_type_ids && pattern.break_type_ids.length > 0) {
      const newBreaks = pattern.break_type_ids.map(breakTypeId => {
        const bt = breakTypes.find(b => b.id === breakTypeId);
        if (!bt) return null;
        
        // Calculate break position in the middle of the shift
        const shiftStart = timeToMinutes(pattern.start_time);
        const shiftEnd = timeToMinutes(pattern.end_time);
        const midpoint = Math.floor((shiftStart + shiftEnd) / 2);
        const breakStart = snapToInterval(minutesToTime(midpoint));
        const breakEnd = minutesToTime(timeToMinutes(breakStart) + bt.duration_minutes);
        
        return {
          break_type_id: breakTypeId,
          start_time: breakStart,
          end_time: snapToInterval(breakEnd)
        };
      }).filter(Boolean);
      
      setBreaks(newBreaks);
    } else {
      setBreaks([]);
    }
    
    // Reset rate selections
    setRateName(null);
    setRateSegments([]);
  }, [shiftPatterns, breakTypes, snapToInterval]);

  // Check if this is a split shift
  const isSplitShift = rateSegments.length > 0;

  // Check if employee is salary-based (no pay rate options for salary employees)
  const isSalaryEmployee = employee?.pay_type === "salary";

  // Get available rates for this employee (only for hourly employees)
  const availableRates = useMemo(() => {
    if (!employee || isSalaryEmployee) return [];
    const rates = [{ name: null, rate: employee.hourly_rate || 0, label: 'Default' }];
    if (employee.additional_rates) {
      employee.additional_rates.forEach(r => {
        rates.push({ name: r.name, rate: r.rate, label: r.name });
      });
    }
    return rates;
  }, [employee, isSalaryEmployee]);

  // Has additional rates available for splits (only for hourly employees)
  const hasAdditionalRates = !isSalaryEmployee && availableRates.length > 1;

  // Calculate shift duration in hours
  const shiftDurationHours = useMemo(() => {
    return calculateDurationHours(startTime, endTime);
  }, [startTime, endTime]);

  // Determine which breaks are required based on shift duration
  // Uses > (greater than) not >= because a 6h shift doesn't need a break, 
  // but 6h 1min does (when min_shift_hours is 6)
  const requiredBreakTypes = useMemo(() => {
    return breakTypes.filter(bt => 
      bt.is_required && shiftDurationHours > bt.min_shift_hours
    );
  }, [breakTypes, shiftDurationHours]);

  // Check if all required breaks are scheduled
  const missingRequiredBreaks = useMemo(() => {
    return requiredBreakTypes.filter(bt => 
      !breaks.some(b => b.break_type_id === bt.id)
    );
  }, [requiredBreakTypes, breaks]);

  // Auto-add required breaks when shift duration exceeds threshold
  // Uses debouncing to avoid adding during intermediate typing states
  useEffect(() => {
    // Only auto-add for NEW shifts, not when editing existing ones
    if (shift) return;
    
    // Debounce: wait 500ms after duration changes before auto-adding
    const timeoutId = setTimeout(() => {
      if (missingRequiredBreaks.length > 0) {
        const newBreaks = missingRequiredBreaks.map(bt => {
          const shiftStart = timeToMinutes(startTime);
          const shiftEnd = timeToMinutes(endTime);
          const midpoint = Math.floor((shiftStart + shiftEnd) / 2);
          const breakStartTime = snapToInterval(minutesToTime(midpoint));
          const breakEndTime = minutesToTime(timeToMinutes(breakStartTime) + bt.duration_minutes);
          
          return {
            break_type_id: bt.id,
            start_time: breakStartTime,
            end_time: snapToInterval(breakEndTime)
          };
        });
        
        setBreaks(prev => {
          // Only add breaks not already present
          const toAdd = newBreaks.filter(nb => 
            !prev.some(b => b.break_type_id === nb.break_type_id)
          );
          return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        });
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [missingRequiredBreaks, startTime, endTime, shift, snapToInterval]);

  const handleStartTimeChange = (newTime) => {
    let snapped = snapToInterval(newTime);
    
    // Constrain to business hours
    if (businessHours) {
      const openMins = timeToMinutes(businessHours.open_time);
      const closeMins = timeToMinutes(businessHours.close_time);
      const snappedMins = timeToMinutes(snapped);
      
      if (snappedMins < openMins) {
        snapped = businessHours.open_time;
      } else if (snappedMins >= closeMins) {
        // Start time must be before close time
        snapped = snapToInterval(minutesToTime(closeMins - snapInterval));
      }
    }
    
    setStartTime(snapped);
    
    // If split shift, adjust first segment's start time
    if (rateSegments.length > 0) {
      setRateSegments(prev => {
        const updated = [...prev];
        updated[0] = { ...updated[0], start_time: snapped };
        return updated;
      });
    }
  };

  const handleEndTimeChange = (newTime) => {
    let snapped = snapToInterval(newTime);
    
    // Constrain to business hours
    if (businessHours) {
      const openMins = timeToMinutes(businessHours.open_time);
      const closeMins = timeToMinutes(businessHours.close_time);
      const snappedMins = timeToMinutes(snapped);
      
      if (snappedMins > closeMins) {
        snapped = businessHours.close_time;
      } else if (snappedMins <= openMins) {
        // End time must be after open time
        snapped = snapToInterval(minutesToTime(openMins + snapInterval));
      }
    }
    
    setEndTime(snapped);
    
    // If split shift, adjust last segment's end time
    if (rateSegments.length > 0) {
      setRateSegments(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { ...updated[updated.length - 1], end_time: snapped };
        return updated;
      });
    }
  };

  // Add a split (convert to split shift or add another segment)
  const addSplit = () => {
    if (rateSegments.length === 0) {
      // Convert to split shift: create two segments
      const midpoint = Math.floor((timeToMinutes(startTime) + timeToMinutes(endTime)) / 2);
      const splitTime = snapToInterval(minutesToTime(midpoint));
      
      setRateSegments([
        { start_time: startTime, end_time: splitTime, rate_name: rateName },
        { start_time: splitTime, end_time: endTime, rate_name: null } // Default rate for second part
      ]);
      setRateName(null); // Clear single rate since we're using segments
    } else {
      // Add another split point in the last segment
      const lastSeg = rateSegments[rateSegments.length - 1];
      const midpoint = Math.floor((timeToMinutes(lastSeg.start_time) + timeToMinutes(lastSeg.end_time)) / 2);
      const splitTime = snapToInterval(minutesToTime(midpoint));
      
      setRateSegments(prev => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        const lastSegment = updated[lastIndex];
        
        // Shorten the last segment
        updated[lastIndex] = { ...lastSegment, end_time: splitTime };
        
        // Add new segment
        updated.push({
          start_time: splitTime,
          end_time: lastSegment.end_time,
          rate_name: null
        });
        
        return updated;
      });
    }
  };

  // Remove split (convert back to single rate or remove a segment)
  const removeSplit = (index) => {
    if (rateSegments.length === 2) {
      // Converting back to single rate shift
      // Use the first segment's rate as the main rate
      setRateName(rateSegments[0].rate_name);
      setRateSegments([]);
    } else {
      // Remove segment and merge with adjacent
      setRateSegments(prev => {
        const updated = [...prev];
        if (index === 0) {
          // Remove first, extend second to cover
          updated[1] = { ...updated[1], start_time: updated[0].start_time };
          updated.splice(0, 1);
        } else {
          // Remove segment, extend previous to cover
          updated[index - 1] = { ...updated[index - 1], end_time: updated[index].end_time };
          updated.splice(index, 1);
        }
        return updated;
      });
    }
  };

  // Update segment rate
  const updateSegmentRate = (index, newRateName) => {
    setRateSegments(prev => prev.map((seg, i) => 
      i === index ? { ...seg, rate_name: newRateName === 'default' ? null : newRateName } : seg
    ));
  };

  // Update segment split time (the end of segment / start of next)
  const updateSplitTime = (segmentIndex, newEndTime) => {
    const snapped = snapToInterval(newEndTime);
    setRateSegments(prev => {
      const updated = [...prev];
      updated[segmentIndex] = { ...updated[segmentIndex], end_time: snapped };
      if (segmentIndex + 1 < updated.length) {
        updated[segmentIndex + 1] = { ...updated[segmentIndex + 1], start_time: snapped };
      }
      return updated;
    });
  };

  // Add a break
  const addBreak = (breakType) => {
    // Default break time to middle of shift
    const startMins = timeToMinutes(startTime);
    const endMins = timeToMinutes(endTime);
    const midpoint = Math.floor((startMins + endMins) / 2);
    const breakStart = minutesToTime(snapToInterval(minutesToTime(midpoint)).split(':').map(Number).reduce((h, m) => h * 60 + m));
    const breakEnd = minutesToTime(timeToMinutes(breakStart) + breakType.duration_minutes);
    
    setBreaks(prev => [...prev, {
      break_type_id: breakType.id,
      start_time: breakStart,
      end_time: snapToInterval(breakEnd)
    }]);
  };

  // Update a break's start time (end time auto-calculates from duration)
  const updateBreakStartTime = (index, value) => {
    const snapped = snapToInterval(value);
    const breakTypeId = breaks[index].break_type_id;
    const breakType = breakTypes.find(bt => bt.id === breakTypeId);
    const duration = breakType?.duration_minutes || 30;
    
    // Calculate end time from start + duration
    const breakEndMins = timeToMinutes(snapped) + duration;
    const breakEnd = minutesToTime(breakEndMins);
    
    setBreaks(prev => prev.map((b, i) => 
      i === index ? { ...b, start_time: snapped, end_time: snapToInterval(breakEnd) } : b
    ));
  };

  // Remove a break
  const removeBreak = (index) => {
    const breakToRemove = breaks[index];
    const breakType = breakTypes.find(bt => bt.id === breakToRemove.break_type_id);
    
    // Check if this is a required break
    if (breakType?.is_required && shiftDurationHours >= breakType.min_shift_hours) {
      // Don't allow removal of required breaks
      return;
    }
    
    setBreaks(prev => prev.filter((_, i) => i !== index));
  };

  // Check for overlap with other shifts (with gap requirement)
  const overlapInfo = useMemo(() => {
    const otherShifts = employeeShifts.filter(s => !shift || s.id !== shift.id);
    
    for (const other of otherShifts) {
      if (doTimesOverlap(startTime, endTime, other.start_time, other.end_time, shiftGapMinutes)) {
        const message = shiftGapMinutes > 0 
          ? `Shifts must be at least ${shiftGapMinutes} minutes apart (conflicts with ${formatTime(other.start_time)} - ${formatTime(other.end_time)})`
          : `Overlaps with shift ${formatTime(other.start_time)} - ${formatTime(other.end_time)}`;
        return {
          hasOverlap: true,
          message
        };
      }
    }
    return { hasOverlap: false, message: null };
  }, [employeeShifts, shift, startTime, endTime, shiftGapMinutes]);

  // Validate shift is within business hours
  const businessHoursValidation = useMemo(() => {
    if (!businessHours) return { isValid: true, message: null };
    
    const shiftStart = timeToMinutes(startTime);
    const shiftEnd = timeToMinutes(endTime);
    const openMins = timeToMinutes(businessHours.open_time);
    const closeMins = timeToMinutes(businessHours.close_time);
    
    if (shiftStart < openMins) {
      return { 
        isValid: false, 
        message: `Start time (${formatTime(startTime)}) is before opening time (${formatTime(businessHours.open_time)})` 
      };
    }
    if (shiftEnd > closeMins) {
      return { 
        isValid: false, 
        message: `End time (${formatTime(endTime)}) is after closing time (${formatTime(businessHours.close_time)})` 
      };
    }
    
    return { isValid: true, message: null };
  }, [businessHours, startTime, endTime]);

  // Validate breaks are within shift time
  const breakValidation = useMemo(() => {
    const shiftStart = timeToMinutes(startTime);
    const shiftEnd = timeToMinutes(endTime);
    
    for (const brk of breaks) {
      const brkStart = timeToMinutes(brk.start_time);
      const brkEnd = timeToMinutes(brk.end_time);
      
      if (brkStart < shiftStart || brkEnd > shiftEnd) {
        return { isValid: false, message: "Break must be within shift time" };
      }
      if (brkStart >= brkEnd) {
        return { isValid: false, message: "Break end time must be after start time" };
      }
    }
    return { isValid: true, message: null };
  }, [breaks, startTime, endTime]);

  // Validate rate segments
  const segmentValidation = useMemo(() => {
    if (rateSegments.length === 0) return { isValid: true, message: null };
    
    for (let i = 0; i < rateSegments.length; i++) {
      const seg = rateSegments[i];
      const segStart = timeToMinutes(seg.start_time);
      const segEnd = timeToMinutes(seg.end_time);
      
      if (segStart >= segEnd) {
        return { isValid: false, message: `Segment ${i + 1} must have end time after start time` };
      }
    }
    return { isValid: true, message: null };
  }, [rateSegments]);

  // Calculate estimated cost
  const estimatedCost = useMemo(() => {
    if (!employee) return 0;
    
    if (isSplitShift) {
      // Sum cost of each segment, accounting for breaks
      return rateSegments.reduce((total, seg) => {
        const segStartMins = timeToMinutes(seg.start_time);
        const segEndMins = timeToMinutes(seg.end_time);
        let segHours = (segEndMins - segStartMins) / 60;
        
        // Deduct unpaid breaks that overlap with this segment
        for (const brk of breaks) {
          const breakType = breakTypes.find(bt => bt.id === brk.break_type_id);
          if (breakType && !breakType.is_paid) {
            const brkStartMins = timeToMinutes(brk.start_time);
            const brkEndMins = timeToMinutes(brk.end_time);
            
            // Calculate overlap
            const overlapStart = Math.max(segStartMins, brkStartMins);
            const overlapEnd = Math.min(segEndMins, brkEndMins);
            if (overlapStart < overlapEnd) {
              segHours -= (overlapEnd - overlapStart) / 60;
            }
          }
        }
        
        const rate = getRateValue(seg.rate_name, employee);
        return total + (segHours * rate);
      }, 0);
    } else {
      // Simple calculation for non-split, accounting for breaks
      let billableHours = shiftDurationHours;
      
      // Deduct unpaid breaks
      for (const brk of breaks) {
        const breakType = breakTypes.find(bt => bt.id === brk.break_type_id);
        if (breakType && !breakType.is_paid) {
          const brkDuration = (timeToMinutes(brk.end_time) - timeToMinutes(brk.start_time)) / 60;
          billableHours -= brkDuration;
        }
      }
      
      const rate = getRateValue(rateName, employee);
      return billableHours * rate;
    }
  }, [employee, rateSegments, rateName, shiftDurationHours, isSplitShift, breaks, breakTypes]);

  const handleSave = () => {
    if (overlapInfo.hasOverlap || !businessHoursValidation.isValid || missingRequiredBreaks.length > 0 || !breakValidation.isValid || !segmentValidation.isValid) {
      return;
    }
    
    const saveData = { 
      start_time: startTime, 
      end_time: endTime, 
      breaks,
      rate_name: isSplitShift ? null : rateName,
      rate_segments: isSplitShift ? rateSegments : []
    };
    
    onSave(saveData);
  };

  const handleDelete = () => {
    onDelete();
  };

  const duration = calculateDuration(startTime, endTime);
  const isValidDuration = parseFloat(duration) > 0;

  // Available break types that aren't already added
  const availableBreakTypes = breakTypes.filter(bt => 
    !breaks.some(b => b.break_type_id === bt.id)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden" data-testid="shift-edit-dialog">
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {shift ? "Edit Shift" : "Add Shift"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-6">
          <div className="space-y-6 py-4">
          {/* Employee Info */}
          {employee && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              {employee.avatar_url ? (
                <img 
                  src={employee.avatar_url} 
                  alt={employee.name}
                  className="w-10 h-10 rounded-full object-cover border border-border"
                />
              ) : (
                <div 
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center font-semibold",
                    !employee.color?.startsWith('#') && `avatar-${employee.color}`
                  )}
                  style={employee.color?.startsWith('#') ? { backgroundColor: employee.color + '20', color: employee.color } : {}}
                >
                  {employee.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <p className="font-medium">{employee.name}</p>
                <p className="text-sm text-muted-foreground">{employee.role}</p>
              </div>
            </div>
          )}

          {/* Predetermined Shift Pattern Selector */}
          {shiftPatterns.length > 0 && !shift && (
            <div className="space-y-2">
              <Label>Use Shift Pattern</Label>
              <Select 
                value={selectedPatternId || 'custom'} 
                onValueChange={applyPattern}
              >
                <SelectTrigger data-testid="shift-pattern-select">
                  <SelectValue placeholder="Select a pattern or use custom times" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom Times</SelectItem>
                  {shiftPatterns.map((pattern) => (
                    <SelectItem key={pattern.id} value={pattern.id}>
                      <div className="flex items-center gap-2">
                        <span>{pattern.name}</span>
                        <span className="text-muted-foreground text-xs tabular-nums">
                          ({formatTime(pattern.start_time)} - {formatTime(pattern.end_time)})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Time Inputs */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-time">Start Time</Label>
              <TimePicker
                value={startTime}
                onChange={handleStartTimeChange}
                minTime={businessHours?.open_time}
                maxTime={businessHours?.close_time}
                snapInterval={snapInterval}
                disabled={isLocked}
                data-testid="shift-start-time"
              />
              <p className="text-xs text-muted-foreground">{formatTime(startTime)}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-time">End Time</Label>
              <TimePicker
                value={endTime}
                onChange={handleEndTimeChange}
                minTime={businessHours?.open_time}
                maxTime={businessHours?.close_time}
                snapInterval={snapInterval}
                disabled={isLocked}
                data-testid="shift-end-time"
              />
              <p className="text-xs text-muted-foreground">{formatTime(endTime)}</p>
            </div>
          </div>

          {/* Pay Rate Section */}
          {hasAdditionalRates && isValidDuration && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Pay Rate</Label>
                {!isSplitShift && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={addSplit}
                    className="h-7 text-xs gap-1"
                    data-testid="add-split-btn"
                  >
                    <Split className="w-3 h-3" />
                    Split Rate
                  </Button>
                )}
              </div>

              {!isSplitShift ? (
                // Single rate selector
                <Select value={rateName || 'default'} onValueChange={(v) => setRateName(v === 'default' ? null : v)}>
                  <SelectTrigger data-testid="shift-rate-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRates.map((r) => (
                      <SelectItem key={r.name || 'default'} value={r.name || 'default'}>
                        <div className="flex items-center justify-between gap-4">
                          <span>{r.label}</span>
                          <span className="text-muted-foreground tabular-nums text-xs">
                            {employee?.hourly_rate > 0 && `${getCurrencySymbol(currency)}${r.rate.toFixed(2)}/hr`}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                // Split rate segments
                <div className="space-y-2">
                  {rateSegments.map((seg, index) => {
                    const segRate = getRateValue(seg.rate_name, employee);
                    const segHours = calculateDurationHours(seg.start_time, seg.end_time);
                    const isLastSegment = index === rateSegments.length - 1;
                    
                    return (
                      <div key={index} className="p-3 rounded-lg border bg-muted/30 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-muted-foreground">
                            Segment {index + 1}: {formatTime(seg.start_time)} - {formatTime(seg.end_time)}
                          </span>
                          {rateSegments.length > 2 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                              onClick={() => removeSplit(index)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        
                        <div className="flex gap-2 items-center">
                          <Select 
                            value={seg.rate_name || 'default'} 
                            onValueChange={(v) => updateSegmentRate(index, v)}
                          >
                            <SelectTrigger className="flex-1 h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {availableRates.map((r) => (
                                <SelectItem key={r.name || 'default'} value={r.name || 'default'}>
                                  <span>{r.label}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                            {getCurrencySymbol(currency)}{segRate.toFixed(2)}/hr
                          </span>
                        </div>

                        {/* Split time adjuster (not for last segment) */}
                        {!isLastSegment && (
                          <div className="flex items-center gap-2 pt-1">
                            <span className="text-xs text-muted-foreground">Split at:</span>
                            <TimePicker
                              value={seg.end_time}
                              onChange={(newTime) => updateSplitTime(index, newTime)}
                              minTime={businessHours?.open_time}
                              maxTime={businessHours?.close_time}
                              snapInterval={snapInterval}
                              className="scale-90 origin-left"
                            />
                          </div>
                        )}
                        
                        <div className="text-xs text-muted-foreground">
                          {segHours.toFixed(2)}h × {getCurrencySymbol(currency)}{segRate.toFixed(2)} = {getCurrencySymbol(currency)}{(segHours * segRate).toFixed(2)}
                        </div>
                      </div>
                    );
                  })}
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={addSplit}
                      className="h-7 text-xs flex-1"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Another Split
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        setRateName(rateSegments[0].rate_name);
                        setRateSegments([]);
                      }}
                      className="h-7 text-xs"
                    >
                      Remove All Splits
                    </Button>
                  </div>
                </div>
              )}
              
              {rateName && !isSplitShift && (
                <p className="text-xs text-muted-foreground">
                  Using <span className="font-medium">{rateName}</span> rate instead of default
                </p>
              )}
            </div>
          )}

          {/* Business hours info */}
          {businessHours && (
            <p className="text-xs text-muted-foreground text-center">
              Business hours: {formatTime(businessHours.open_time)} - {formatTime(businessHours.close_time)} • 
              Times snap to {snapInterval} minute intervals
            </p>
          )}

          {/* Breaks Section */}
          {breakTypes.length > 0 && isValidDuration && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Breaks</Label>
                {availableBreakTypes.length > 0 && (
                  <Select onValueChange={(id) => {
                    const bt = breakTypes.find(b => b.id === id);
                    if (bt) addBreak(bt);
                  }}>
                    <SelectTrigger className="w-36 h-8 text-xs" data-testid="add-break-select">
                      <Plus className="w-3 h-3 mr-1" />
                      <span>Add Break</span>
                    </SelectTrigger>
                    <SelectContent>
                      {availableBreakTypes.map(bt => {
                        const IconComp = getBreakIcon(bt.icon);
                        return (
                          <SelectItem key={bt.id} value={bt.id}>
                            <div className="flex items-center gap-2">
                              <IconComp className="w-3 h-3" />
                              <span>{bt.name}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Required breaks warning */}
              {missingRequiredBreaks.length > 0 && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-600">Required Breaks</p>
                    <p className="text-xs text-amber-600/80">
                      Shifts of {shiftDurationHours.toFixed(1)}h require: {missingRequiredBreaks.map(b => b.name).join(', ')}
                    </p>
                  </div>
                </div>
              )}

              {/* Scheduled breaks */}
              {breaks.map((brk, index) => {
                const breakType = breakTypes.find(bt => bt.id === brk.break_type_id);
                if (!breakType) return null;
                
                const IconComp = getBreakIcon(breakType.icon);
                const isRequired = breakType.is_required && shiftDurationHours > breakType.min_shift_hours;
                
                return (
                  <div key={index} className="p-3 rounded-lg border bg-muted/30 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <IconComp className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{breakType.name}</span>
                        {isRequired && (
                          <span className="text-xs bg-amber-500/20 text-amber-600 px-1.5 py-0.5 rounded">Required</span>
                        )}
                      </div>
                      {!isRequired && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => removeBreak(index)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Start</Label>
                        <TimePicker
                          value={brk.start_time}
                          onChange={(newTime) => updateBreakStartTime(index, newTime)}
                          minTime={startTime}
                          maxTime={endTime}
                          snapInterval={snapInterval}
                          className="scale-90 origin-left"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">End ({breakType.duration_minutes} min)</Label>
                        <div className="h-8 px-3 flex items-center rounded-md border bg-muted/50 text-sm tabular-nums text-muted-foreground">
                          {formatTime(brk.end_time)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {breaks.length === 0 && missingRequiredBreaks.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  No breaks scheduled
                </p>
              )}
            </div>
          )}

          {/* Overlap Warning */}
          {overlapInfo.hasOverlap && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-destructive">Shift Overlap</p>
                <p className="text-xs text-destructive/80">{overlapInfo.message}</p>
              </div>
            </div>
          )}

          {/* Business Hours Warning */}
          {!businessHoursValidation.isValid && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-destructive">Outside Business Hours</p>
                <p className="text-xs text-destructive/80">{businessHoursValidation.message}</p>
              </div>
            </div>
          )}

          {/* Invalid Duration Warning */}
          {!isValidDuration && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-destructive">Invalid Time</p>
                <p className="text-xs text-destructive/80">End time must be after start time</p>
              </div>
            </div>
          )}

          {/* Break Validation Warning */}
          {!breakValidation.isValid && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-destructive">Invalid Break</p>
                <p className="text-xs text-destructive/80">{breakValidation.message}</p>
              </div>
            </div>
          )}

          {/* Segment Validation Warning */}
          {!segmentValidation.isValid && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-destructive">Invalid Rate Segment</p>
                <p className="text-xs text-destructive/80">{segmentValidation.message}</p>
              </div>
            </div>
          )}

          {/* Duration & Cost Display */}
          <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-muted-foreground">Shift Duration</p>
                <p className="text-2xl font-bold tabular-nums">{isValidDuration ? `${duration} hours` : '—'}</p>
              </div>
              {employee?.hourly_rate > 0 && isValidDuration && (
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Est. Cost</p>
                  <p className="text-xl font-bold tabular-nums text-primary">
                    {getCurrencySymbol(currency)}{estimatedCost.toFixed(2)}
                  </p>
                </div>
              )}
            </div>
            {breaks.length > 0 && isValidDuration && !isSplitShift && (
              <p className="text-xs text-muted-foreground mt-2">
                Working time: {(shiftDurationHours - breaks.reduce((acc, b) => {
                  return acc + (timeToMinutes(b.end_time) - timeToMinutes(b.start_time)) / 60;
                }, 0)).toFixed(2)}h (excluding breaks)
              </p>
            )}
            {isSplitShift && (
              <p className="text-xs text-muted-foreground mt-2">
                Split across {rateSegments.length} rate segments
              </p>
            )}
          </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 px-6 pb-6 pt-4 border-t flex gap-2 sm:gap-0">
          {/* Show locked indicator when viewing past shift */}
          {isLocked && shift && (
            <div className="flex items-center gap-2 mr-auto text-muted-foreground text-sm">
              <Lock className="w-4 h-4" />
              <span>Past rotas are read-only</span>
            </div>
          )}
          {shift && !isLocked && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="mr-auto"
              data-testid="shift-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {isLocked ? "Close" : "Cancel"}
          </Button>
          {!isLocked && (
            <Button 
              onClick={handleSave} 
              disabled={overlapInfo.hasOverlap || !isValidDuration || !businessHoursValidation.isValid || missingRequiredBreaks.length > 0 || !breakValidation.isValid || !segmentValidation.isValid}
              data-testid="shift-save-btn"
            >
              {shift ? "Update" : "Add"} Shift
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftEditDialog;
